// LT-393 — Contact cockpit (replaces the legacy ContactDetailPage).
// Puts the 11-stage lifecycle ribbon front-and-centre and surfaces the
// §9.6 prior-call composer as a right-side drawer the operator can
// audit before dispatching a call. Tabs replace the long vertical
// scroll on the legacy page.
//
// State machine sketch (parent owns it; sub-components are stateless):
//   - tab: 'overview' | 'activity' | 'enrolments' | 'calls'
//   - stage: lifecycle stage (mirrors contact.lifecycle_stage)
//   - transitioning: target stage during ConfirmReason modal | null
//   - transitionError: invalid-transition descriptor | null  (toast)
//   - summary: committed L4 prior-call summary  (persists between dispatches)
//   - callState: null | 'preview' | 'composing'  (composing → connected)
//
// Backend wiring TODOs (see prompt §"Wire the API"):
//   - L4 override persistence: backend endpoint to override the prior-call
//     summary for a contact. Until shipped, override stays in component
//     state and writes a manual audit row via createNote.
//   - Preview composer endpoint: backend should return the composed prompt
//     for a contact without dispatching, so the live composer matches the
//     preview byte-for-byte. Until shipped, we compose client-side using
//     composedPrompt.ts (same shape, just running locally).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Phone, Eye, Pencil, Trash2 } from 'lucide-react';

import PageLoader from '../../../components/PageLoader';
import PageShell from '../../../components/PageShell';
import LifecycleHistoryTimeline from '../../../components/LifecycleHistoryTimeline';
import ScheduledCallbacksSection from '../../../components/ScheduledCallbacksSection';
import ContactActiveSequencePanel from '../../../components/ContactActiveSequencePanel';
import { accountsAPI } from '../../../services/accountsAPI';
import { callsAPIv2, type CallSummaryListItem } from '../../../services/callsAPI';
import {
  contactsAPI,
  type PriorCallPreviewResponse,
} from '../../../services/contactsAPI';
import { opportunitiesAPI } from '../../../services/opportunitiesAPI';
import {
  Button,
  Card,
  ConfirmReasonModal,
  Pill,
  Tabs,
  Textarea,
} from '../../../ui';
import type {
  Account,
  Activity,
  Contact,
  LifecycleStage,
} from '../../../types/contact';
import { LIFECYCLE_STAGE_LABELS } from '../../../types/contact';
import type { Opportunity } from '../../../types/pipeline';
import EditContactModal, {
  type ContactDetailsPatch,
  type ContactIntelligencePatch,
} from '../EditContactModal';

import ContactCallNowOverlay from './ContactCallNowOverlay';
import ContactInvalidTransitionToast, {
  type InvalidTransition,
} from './ContactInvalidTransitionToast';
import ContactLifecycleRibbon from './ContactLifecycleRibbon';
import ContactPreviewComposerModal from './ContactPreviewComposerModal';
import ContactPriorCallDrawer from './ContactPriorCallDrawer';
import {
  buildLayerSummaries,
  composePrompt,
  type PriorCallLayer,
} from './composedPrompt';
import { getReachableTargets } from './lifecycleTransitions';

type CockpitTab = 'overview' | 'activity' | 'enrolments' | 'calls';

const TAB_ITEMS: { value: CockpitTab; label: string }[] = [
  { value: 'overview', label: 'Overview' },
  { value: 'activity', label: 'Activity' },
  { value: 'enrolments', label: 'Enrolments' },
  { value: 'calls', label: 'Calls' },
];

function fullNameOf(c: Contact): string {
  return [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || '(no name)';
}

function initialsOf(c: Contact): string {
  const first = c.first_name?.[0] ?? '';
  const last = c.last_name?.[0] ?? '';
  return (first + last).toUpperCase() || '??';
}

function summarizeCall(c: CallSummaryListItem): string {
  const dur = typeof c.call_cost === 'number' ? `${c.call_cost.toFixed(2)} cr` : null;
  const head = [
    c.call_direction ? c.call_direction.charAt(0).toUpperCase() + c.call_direction.slice(1) : 'Call',
    new Date(c.timestamp).toLocaleString(),
    dur,
  ]
    .filter(Boolean)
    .join(' · ');
  return head;
}

const ContactCockpitPage: React.FC = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();

  const [contact, setContact] = useState<Contact | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [calls, setCalls] = useState<CallSummaryListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Cockpit-specific state.
  const [tab, setTab] = useState<CockpitTab>('overview');
  const [transitioning, setTransitioning] = useState<LifecycleStage | null>(null);
  const [transitionError, setTransitionError] = useState<InvalidTransition | null>(null);
  const [overrideConfirm, setOverrideConfirm] = useState<{ next: string } | null>(
    null,
  );
  // L4 prior-call summary — sourced from the backend preview endpoint
  // (LT-393) so the cockpit, the live composer, and the dispatch path
  // agree on the same string. Initial value comes from the response;
  // local state mirrors the backend until the next save.
  const [priorCallSummary, setPriorCallSummary] = useState<string>('');
  const [previewState, setPreviewState] = useState<PriorCallPreviewResponse | null>(
    null,
  );
  const [callState, setCallState] = useState<'preview' | 'composing' | null>(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  // CCT-16: delete from the detail page (soft-delete via DELETE
  // /contacts/{id}), confirm-gated. On success we leave for the list.
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!contactId) return;
    setDeleting(true);
    try {
      await contactsAPI.softDelete(contactId);
      navigate('/contacts');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Delete failed.');
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  const fetchAll = useCallback(async () => {
    if (!contactId) return;
    setLoading(true);
    setErr(null);
    try {
      const [c, accs, acts, opps, callsResp, preview] = await Promise.all([
        contactsAPI.get(contactId),
        accountsAPI.list({ limit: 200 }).catch(() => [] as Account[]),
        contactsAPI.listActivities(contactId).catch(() => [] as Activity[]),
        opportunitiesAPI
          .listForContact(contactId)
          .catch(() => [] as Opportunity[]),
        callsAPIv2.list({ contact_id: contactId, limit: 25 }).catch(
          () => ({ calls: [] as CallSummaryListItem[], total: 0, skip: 0, limit: 25 }),
        ),
        // Preview endpoint resolves the §9.6 layers + composed prompt
        // for this contact (uses operator override when present, else
        // the latest analyzer summary). Falls back to null on error so
        // the cockpit still renders if the backend hop is flaky.
        contactsAPI
          .getPriorCallPreview(contactId)
          .catch(() => null as PriorCallPreviewResponse | null),
      ]);
      setContact(c);
      setAccounts(accs);
      setActivities(acts);
      setOpportunities(opps);
      setCalls(callsResp.calls);
      setPreviewState(preview);
      if (preview) {
        // Use the backend's resolved L4 summary — when the contact has
        // an operator override, this is the override; otherwise the
        // latest analyzer summary; otherwise the empty/fallback string.
        setPriorCallSummary(preview.layers.L4 ?? '');
      } else {
        const latest = callsResp.calls[0];
        if (latest?.analysis_summary) {
          setPriorCallSummary(latest.analysis_summary);
        }
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load contact');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const stage: LifecycleStage = contact?.lifecycle_stage ?? 'cold';

  const accountName = useMemo(() => {
    if (!contact?.account_id) return '';
    return accounts.find((a) => a.id === contact.account_id)?.name ?? '';
  }, [contact, accounts]);

  const fullName = contact ? fullNameOf(contact) : '';

  const layerDetails = useMemo<Record<PriorCallLayer['id'], string>>(() => {
    // Prefer the backend's resolved layer details (§9.6 composer is the
    // wire-truth for what the agent will see). Fall back to client-side
    // composition if the preview endpoint failed — keeps the cockpit
    // usable in offline / flaky-backend scenarios.
    if (previewState?.layers) {
      return {
        L1: previewState.layers.L1,
        L2: previewState.layers.L2,
        L3: previewState.layers.L3,
        L4: priorCallSummary || previewState.layers.L4,
        L5: previewState.layers.L5,
      };
    }
    if (!contact) {
      return { L1: '', L2: '', L3: '', L4: '', L5: '' };
    }
    return buildLayerSummaries({
      contact,
      stage,
      priorCallCount: calls.length,
      priorCallLastDuration: null,
      priorCallLastDisposition: calls[0]?.disposition ?? null,
    });
  }, [previewState, priorCallSummary, contact, stage, calls]);

  const composedPromptText = useMemo(() => {
    // Backend wins when present so the preview matches what dispatch
    // will see. Re-compose locally only when the preview hop failed.
    if (previewState?.composed_prompt) return previewState.composed_prompt;
    if (!contact) return '';
    return composePrompt({
      contact,
      stage,
      priorCallSummary,
      priorCallCount: calls.length,
      campaignPosition: null,
      callbackHint: null,
    });
  }, [previewState, contact, stage, priorCallSummary, calls.length]);

  // ---- Lifecycle transitions ------------------------------------------------

  const handleStageSelect = (target: LifecycleStage, isValid: boolean) => {
    if (!contact) return;
    if (target === stage) return;
    if (!isValid) {
      setTransitionError({
        from: stage,
        to: target,
        valid: getReachableTargets(stage),
      });
      return;
    }
    setTransitioning(target);
  };

  const commitTransition = async (reason: string) => {
    if (!contact || !transitioning || !contactId) return;
    setSaving(true);
    setErr(null);
    try {
      const updated = await contactsAPI.update(contactId, {
        lifecycle_stage: transitioning,
      });
      setContact(updated);
      // Append a manual audit row referencing the transition + reason so the
      // Activity tab carries it. The lifecycle-history endpoint also picks
      // it up server-side (LT-360 audit trail).
      try {
        const note = await contactsAPI.createNote(contactId, {
          subject: `Lifecycle: ${stage} → ${transitioning}`,
          body: reason || '(no reason supplied)',
        });
        setActivities((prev) => [note, ...prev]);
      } catch {
        /* swallow — the lifecycle endpoint already wrote the audit entry. */
      }
      setTransitioning(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Transition failed');
    } finally {
      setSaving(false);
    }
  };

  // ---- L4 override ----------------------------------------------------------

  const requestSummaryOverride = (next: string) => {
    setOverrideConfirm({ next });
  };

  const commitSummaryOverride = async (reason: string) => {
    if (!overrideConfirm || !contactId) return;
    setSaving(true);
    setErr(null);
    try {
      // Backend persists the override + appends the audit note in one
      // round-trip (LT-393). Re-fetch the preview so the drawer + the
      // composed-prompt overlay reflect the new L4 immediately.
      const trimmed = overrideConfirm.next.trim();
      const updated = await contactsAPI.setPriorCallOverride(contactId, {
        summary: trimmed === '' ? null : trimmed,
        reason: reason || undefined,
      });
      setContact(updated);
      setPriorCallSummary(trimmed);
      try {
        const [acts, preview] = await Promise.all([
          contactsAPI.listActivities(contactId),
          contactsAPI.getPriorCallPreview(contactId),
        ]);
        setActivities(acts);
        setPreviewState(preview);
        setPriorCallSummary(preview.layers.L4 ?? trimmed);
      } catch {
        /* non-fatal — primary save succeeded; refresh on next render */
      }
      setOverrideConfirm(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Override failed');
    } finally {
      setSaving(false);
    }
  };

  // ---- Notes ----------------------------------------------------------------

  const addNote = async () => {
    if (!contactId || !noteBody.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      const note = await contactsAPI.createNote(contactId, {
        body: noteBody.trim(),
      });
      setActivities((prev) => [note, ...prev]);
      setNoteBody('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  // ---- Render ---------------------------------------------------------------

  if (loading) return <PageLoader />;
  if (!contact) {
    return (
      <PageShell className="p-6">
        <div className="text-danger-text">{err || 'Contact not found.'}</div>
        <Button intent="ghost" className="mt-4" onClick={() => navigate('/contacts')}>
          Back to Contacts
        </Button>
      </PageShell>
    );
  }

  const callbackPill = stage === 'callback_scheduled' ? (
    <Pill intent="info">Callback scheduled</Pill>
  ) : null;

  const saveIntelligence = async (patch: ContactIntelligencePatch) => {
    if (!contactId) return;
    setSaving(true);
    try {
      const updated = await contactsAPI.update(contactId, patch);
      setContact(updated);
      setEditModalOpen(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to update intelligence');
    } finally {
      setSaving(false);
    }
  };

  const saveDetails = async (patch: ContactDetailsPatch) => {
    if (!contactId) return;
    setSaving(true);
    try {
      const updated = await contactsAPI.update(contactId, patch);
      setContact(updated);
      setEditModalOpen(false);
    } catch (e) {
      // Surface backend EmailStr / validation failures without dismissing
      // the modal — operator can fix the input and retry.
      setErr(e instanceof Error ? e.message : 'Failed to update details');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell className="relative px-4 lg:px-6 py-4 lg:py-5 max-w-[1280px] mx-auto">
      <button
        onClick={() => navigate('/contacts')}
        className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-hover hover:underline mb-3"
      >
        <ArrowLeft size={14} /> Contacts
      </button>

      {err && (
        <div className="mb-3 text-sm text-danger-text border border-danger-border rounded-ds-lg px-3 py-2 bg-danger-tint">
          {err}
        </div>
      )}

      {/* Identity row */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-semibold text-white shrink-0"
          style={{
            background:
              'linear-gradient(135deg, oklch(70% 0.13 240), oklch(60% 0.15 280))',
          }}
        >
          {initialsOf(contact)}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-xl font-semibold text-slate-50">{fullName}</div>
          <div className="text-[13px] text-slate-400">
            {[contact.title, accountName].filter(Boolean).join(' · ') ||
              '(no role / account)'}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
            {[contact.phone, contact.email].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
        {callbackPill}
        <Button intent="ghost" size="sm" onClick={() => setCallState('preview')}>
          <Eye className="w-3.5 h-3.5" />
          <span>Preview composer</span>
        </Button>
        <Button
          intent="secondary"
          size="sm"
          onClick={() => setEditModalOpen(true)}
          disabled={saving}
        >
          <Pencil className="w-3.5 h-3.5" />
          <span>Edit</span>
        </Button>
        <Button
          intent="ghost"
          size="sm"
          onClick={() => setDeleteConfirmOpen(true)}
          disabled={saving || deleting}
        >
          <Trash2 className="w-3.5 h-3.5" />
          <span>Delete</span>
        </Button>
        <Button intent="primary" size="sm" onClick={() => setCallState('composing')}>
          <Phone className="w-3.5 h-3.5" />
          <span>Call now</span>
        </Button>
      </div>

      {/* Lifecycle ribbon */}
      <ContactLifecycleRibbon stage={stage} onSelect={handleStageSelect} />

      {/* Two-column body */}
      <div className="grid gap-3 lg:[grid-template-columns:1fr_380px] items-start">
        {/* Left: tabs */}
        <Card padding="default" className="overflow-hidden">
          <Tabs
            items={TAB_ITEMS}
            value={tab}
            onChange={(v) => setTab(v as CockpitTab)}
            aria-label="Contact cockpit sections"
            className="-mt-1 mb-4"
          />

          {tab === 'overview' && (
            <div className="space-y-5">
              <div>
                <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">
                  Notes
                </div>
                <Textarea
                  rows={3}
                  placeholder="Add a note about this contact…"
                  value={noteBody}
                  onChange={(e) => setNoteBody(e.target.value)}
                  disabled={saving}
                />
                <div className="mt-2 flex justify-end">
                  <Button
                    intent="primary"
                    size="sm"
                    onClick={addNote}
                    disabled={saving || !noteBody.trim()}
                  >
                    Add note
                  </Button>
                </div>
              </div>

              <div>
                <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">
                  Linked deals
                </div>
                {opportunities.length === 0 ? (
                  <div className="text-sm text-slate-400">
                    This contact isn’t the primary on any opportunity yet.
                  </div>
                ) : (
                  <ul className="divide-y divide-white/[0.06]">
                    {opportunities.map((o) => (
                      <li key={o.id} className="py-2 flex items-center justify-between gap-3">
                        <Link
                          to={`/deals/${o.id}`}
                          className="text-accent hover:text-accent-hover hover:underline font-medium"
                        >
                          {o.name}
                        </Link>
                        <div className="text-xs text-slate-400 flex gap-3 font-mono">
                          {o.amount != null && (
                            <span>
                              {o.currency ?? ''} {Number(o.amount).toLocaleString()}
                            </span>
                          )}
                          {o.close_date && (
                            <span>Close {new Date(o.close_date).toLocaleDateString()}</span>
                          )}
                          {o.closed_at && <span className="text-ok-text">closed</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {tab === 'activity' && (
            <div className="space-y-4">
              {contactId ? <LifecycleHistoryTimeline contactId={contactId} /> : null}
              <div>
                <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400 mb-2">
                  Activity timeline
                </div>
                {activities.length === 0 ? (
                  <div className="text-sm text-slate-400">No activity yet.</div>
                ) : (
                  <ul className="divide-y divide-white/[0.06]">
                    {activities.map((a) => (
                      <li key={a.id} className="py-2.5">
                        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">
                          {a.type} · {new Date(a.occurred_at).toLocaleString()}
                        </div>
                        {a.subject ? (
                          <div className="text-sm font-medium text-slate-50 mt-0.5">
                            {a.subject}
                          </div>
                        ) : null}
                        {a.body ? (
                          <div className="text-sm text-slate-300 whitespace-pre-wrap mt-1">
                            {a.body}
                          </div>
                        ) : a.summary ? (
                          <div className="text-sm text-slate-300 mt-1">{a.summary}</div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {tab === 'enrolments' && (
            <div className="space-y-3">
              {contactId ? <ContactActiveSequencePanel contactId={contactId} /> : null}
              {contactId ? <ScheduledCallbacksSection contactId={contactId} /> : null}
            </div>
          )}

          {tab === 'calls' && (
            <div className="space-y-2">
              {calls.length === 0 ? (
                <div className="text-sm text-slate-400">No calls on file yet.</div>
              ) : (
                calls.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => navigate(`/calls/${c.id}`)}
                    className="w-full text-left rounded-ds-md bg-surface-2 border border-white/[0.06] p-3 hover:border-white/20 hover:bg-surface-3 transition-colors focus:outline-none focus:ring-2 focus:ring-accent/50"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] font-semibold text-slate-50">
                        {summarizeCall(c)}
                      </span>
                      {c.disposition ? (
                        <Pill intent="info">{c.disposition}</Pill>
                      ) : null}
                    </div>
                    {c.analysis_summary ? (
                      <p className="text-xs text-slate-300 mt-2 italic leading-relaxed">
                        {c.analysis_summary}
                      </p>
                    ) : null}
                  </button>
                ))
              )}
            </div>
          )}
        </Card>

        {/* Right: prior-call drawer */}
        <ContactPriorCallDrawer
          layerDetails={layerDetails}
          summary={priorCallSummary}
          saving={saving}
          onSaveOverride={(next) => requestSummaryOverride(next)}
        />
      </div>

      {/* Lifecycle confirm-with-reason modal */}
      <ConfirmReasonModal
        open={transitioning !== null}
        title={
          transitioning ? (
            <>
              Move lifecycle to{' '}
              <code className="font-mono text-accent-text">
                {LIFECYCLE_STAGE_LABELS[transitioning]}
              </code>
              ?
            </>
          ) : (
            ''
          )
        }
        description="Transition will be written to the audit log with your reason. Some terminal stages (customer, disqualified, dormant) require an opportunity or reason side-effect."
        reasonPlaceholder="Why this transition?"
        confirmLabel="Commit transition"
        saving={saving}
        onCancel={() => setTransitioning(null)}
        onConfirm={commitTransition}
      />

      {/* L4 override confirm */}
      <ConfirmReasonModal
        open={overrideConfirm !== null}
        title="Override prior-call summary?"
        description={
          <>
            The new summary will be used by the prior-call composer on the next dispatch
            instead of the analyzer’s most-recent output. Saving captures an
            audit entry.
          </>
        }
        reasonPlaceholder="Why override?"
        confirmLabel="Save override"
        saving={saving}
        onCancel={() => setOverrideConfirm(null)}
        onConfirm={commitSummaryOverride}
      />

      {/* Preview composer overlay */}
      <ContactPreviewComposerModal
        open={callState === 'preview'}
        contactName={fullName}
        layerDetails={layerDetails}
        composedPrompt={composedPromptText}
        onClose={() => setCallState(null)}
        onCallNow={() => setCallState('composing')}
      />

      {/* Call-now overlay */}
      <ContactCallNowOverlay
        open={callState === 'composing'}
        contactName={fullName}
        onClose={() => setCallState(null)}
        onConnected={() => {
          // Append a manual audit row marking the dispatch.
          if (!contactId) return;
          contactsAPI
            .createNote(contactId, {
              subject: 'Call dispatched · prior-call composer',
              body: 'Operator initiated a call · prior-call composer ran 5/5 layers.',
            })
            .then((note) => setActivities((prev) => [note, ...prev]))
            .catch(() => {
              /* non-fatal */
            });
        }}
      />

      {/* Invalid-transition toast */}
      <ContactInvalidTransitionToast
        error={transitionError}
        onDismiss={() => setTransitionError(null)}
      />

      {/* Tabbed Details / Classification edit modal (LT-471). */}
      <EditContactModal
        isOpen={editModalOpen}
        contact={contact}
        saving={saving}
        onClose={() => setEditModalOpen(false)}
        onSaveDetails={saveDetails}
        onSaveClassification={saveIntelligence}
      />

      {deleteConfirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => !deleting && setDeleteConfirmOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="contact-cockpit-delete-title"
        >
          <div
            className="w-full max-w-md bg-surface-1 border border-white/[0.08] rounded-ds-xl p-5 shadow-ds-e3"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="contact-cockpit-delete-title"
              className="text-lg font-semibold text-slate-50 mb-2"
            >
              Delete contact
            </h2>
            <p className="text-sm text-slate-300">
              Delete <span className="font-semibold text-slate-50">{fullName}</span>?
              They'll be removed from your contacts list. Past call and activity
              history is kept.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button
                intent="ghost"
                onClick={() => !deleting && setDeleteConfirmOpen(false)}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button intent="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default ContactCockpitPage;
